<template>
  <div class="top-months-list">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <div v-if="data.length === 0" class="text-center py-4">
        <a-empty :description="$t('financialStats.noData')" />
      </div>
      <div v-else>
        <div 
          v-for="(month, index) in data" 
          :key="index"
          class="month-item"
          :class="{ 'top-month': index === 0 }"
        >
          <div class="month-rank">
            <div class="rank-badge" :class="getRankClass(index)">
              {{ index + 1 }}
            </div>
          </div>
          
          <div class="month-info">
            <div class="month-name">
              {{ month.label || getMonthName(month.month) }}
            </div>
            <div class="month-stats">
              <div class="stat-item">
                <span class="stat-label">{{ $t('financialStats.revenue') }}:</span>
                <span class="stat-value revenue">{{ formatCurrency(month.revenue) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('financialStats.profit') }}:</span>
                <span class="stat-value profit" :class="{ negative: month.profit < 0 }">
                  {{ formatCurrency(month.profit) }}
                </span>
              </div>
            </div>
          </div>
          
          <div class="month-performance">
            <a-progress
              :percent="getPerformancePercent(month.profit)"
              :showInfo="false"
              :strokeWidth="6"
              :strokeColor="getPerformanceColor(month.profit)"
            />
            <div class="performance-text">
              {{ getPerformanceText(month.profit) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "TopMonthsList",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  methods: {
    getMonthName(monthNumber) {
      return moment.months()[monthNumber - 1];
    },
    
    formatCurrency(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + `M ${this.currency}`;
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + `K ${this.currency}`;
      }
      return value.toLocaleString() + ` ${this.currency}`;
    },
    
    getRankClass(index) {
      const classes = ['rank-gold', 'rank-silver', 'rank-bronze'];
      return classes[index] || 'rank-default';
    },
    
    getPerformancePercent(profit) {
      if (this.data.length === 0) return 0;
      const maxProfit = Math.max(...this.data.map(m => m.profit));
      const minProfit = Math.min(...this.data.map(m => m.profit));
      const range = maxProfit - minProfit;
      
      if (range === 0) return 50;
      return ((profit - minProfit) / range) * 100;
    },
    
    getPerformanceColor(profit) {
      if (profit >= 0) return '#52c41a';
      return '#ff4d4f';
    },
    
    getPerformanceText(profit) {
      if (profit >= 0) {
        return this.$t('financialStats.positivePerformance');
      }
      return this.$t('financialStats.negativePerformance');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

.top-months-list {
  .month-item {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 12px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    transition: all 0.3s ease;
    
    &:hover {
      background: #f5f5f5;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    &.top-month {
      background: linear-gradient(135deg, #fff7e6 0%, #fff2cc 100%);
      border-color: #faad14;
      
      .rank-badge.rank-gold {
        background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
        color: #8b6914;
        font-weight: 700;
      }
    }
    
    .month-rank {
      margin-right: 16px;
      
      .rank-badge {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        
        &.rank-gold {
          background: #faad14;
          color: white;
        }
        
        &.rank-silver {
          background: #8c8c8c;
          color: white;
        }
        
        &.rank-bronze {
          background: #d46b08;
          color: white;
        }
        
        &.rank-default {
          background: #f0f0f0;
          color: #8c8c8c;
        }
      }
    }
    
    .month-info {
      flex: 1;
      
      .month-name {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 4px;
      }
      
      .month-stats {
        .stat-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
          
          .stat-label {
            font-size: 12px;
            color: #8c8c8c;
          }
          
          .stat-value {
            font-size: 12px;
            font-weight: 600;
            
            &.revenue {
              color: #52c41a;
            }
            
            &.profit {
              color: #1890ff;
              
              &.negative {
                color: #ff4d4f;
              }
            }
          }
        }
      }
    }
    
    .month-performance {
      width: 120px;
      margin-left: 16px;
      
      .performance-text {
        font-size: 10px;
        color: #8c8c8c;
        text-align: center;
        margin-top: 4px;
      }
    }
  }
}
</style>
